import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Navigation from '../components/Navigation';
import Header from '../components/Header';
import FacilitiesPage from './FacilitiesPage';
import SectionsPage from './SectionsPage';
import EquipmentPage from './EquipmentPage';
import AdminPage from './AdminPage';
import MyBookingsPage from './MyBookingsPage';
import RafflePage from './RafflePage';
import { bookingService } from '../services/bookingService';
import { adminService } from '../services/adminService';
import { sectionService } from '../services/sectionService';
import { raffleService } from '../services/raffleService';
import { useBookingData } from '../hooks/useBookingData';
import RaffleCountdownPopup from '../components/RaffleCountdownPopup';
import GeneralRafflePage from './GeneralRafflePage';
import './BookingPage.css';

const BookingPage = () => {
  // ==========================================================================
  // STATE MANAGEMENT
  // ==========================================================================
  
  // UI State
  const [activeTab, setActiveTab] = useState('facilities');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Raffle Popup State
  const [showRafflePopup, setShowRafflePopup] = useState(false);
  const [activeRaffleEvent, setActiveRaffleEvent] = useState(null);
  
  // User State
  const [user, setUser] = useState(null);
  const [userRoles, setUserRoles] = useState({
    isAdmin: false,
    isPresident: false,
    isVicePresident: false,
    isSecretary: false,
    isTreasurer: false
  });

  // Data from API - Fetched from SQL
  const [facilitiesData, setFacilitiesData] = useState([]);
  const [sectionsData, setSectionsData] = useState([]);
  const [equipmentData, setEquipmentData] = useState([]);
  const [lookupData, setLookupData] = useState({
    jerseySizes: [],
    timeSlots: [],
    eventTypes: [],
    bookingStatuses: []
  });

  // ==========================================================================
  // BOOKING MODAL STATE - ADDED HERE
  // ==========================================================================
  
  // Booking Modal State
  const [bookingData, setBookingData] = useState({
    date: '',
    endDate: '',        // for multi-day events
    startTime: '',
    endTime: '',
    eventType: 'private',
    purpose: '',        // purpose of booking
    guests: 1,
    phone: '',
    whatsapp: '',
    notes: '',
    equipment: [],
    clearanceFile: null,
    commitmentFile: null,
    residentialArea: false,
    noiseAfter9pm: false
  });
  
  const [bookingStep, setBookingStep] = useState(1);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [approvalFile, setApprovalFile] = useState(null);
  const [approvalFileName, setApprovalFileName] = useState('');

  // ==========================================================================
  // CUSTOM HOOKS
  // ==========================================================================
  
  const {
    // Bookings
    myBookings = [],
    pendingBookings = [],
    approvedBookings = [],
    rejectedBookings = [],
    cancelledBookings = [],
    
    // Members
    sectionMembers = {},
    pendingMembers = [],
    approvedMembers = [],
    
    // Notifications
    notifications = [],
    setNotifications = () => {},
    addNotification = () => {},
    
    // Filters
    filterStatus = 'all',
    setFilterStatus = () => {},
    filterType = 'all',
    setFilterType = () => {},
    filterFacility = 'all',
    setFilterFacility = () => {},
    searchTerm = '',
    setSearchTerm = () => {},
    dateRange = { start: '', end: '' },
    setDateRange = () => {},
    sortBy = 'date_desc',
    setSortBy = () => {},
    
    // Selection
    selectedBookings = [],
    setSelectedBookings = () => {},
    toggleSelectBooking = () => {},
    toggleSelectAll = () => {},
    
    // Waitlist
    waitlistBookings = [],
    addToWaitlist = () => {},
    removeFromWaitlist = () => {},
    promoteFromWaitlist = () => {},
    
    // Delegation
    delegatedUsers = [],
    presidentDelegation = false,
    setPresidentDelegation = () => {},
    handleDelegate = () => {},
    
    // Logs
    auditLog = [],
    addAuditLog = () => {},
    communicationHistory = [],
    sendSMS = () => {},
    sendWhatsApp = () => {},
    sendEmail = () => {},
    locationChangeHistory = [],
    handleLocationChange = () => {},
    
    // Conflicts
    conflicts = [],
    detectConflicts = () => {},
    resolveConflict = () => {},
    
    // Feedback
    feedbacks = [],
    submitFeedback = () => {},
    respondToFeedback = () => {},
    
    // Maintenance
    maintenanceSchedule = [],
    scheduleMaintenance = () => {},
    cancelMaintenance = () => {},
    blackoutDates = [],
    addBlackoutDate = () => {},
    removeBlackoutDate = () => {},
    
    // Pricing
    pricingRules = [],
    addPricingRule = () => {},
    updatePricingRule = () => {},
    deletePricingRule = () => {},
    
    // Permissions
    permissions = {},
    updateRolePermissions = () => {},
    
    // Announcements
    announcements = [],
    createAnnouncement = () => {},
    deleteAnnouncement = () => {},
    
    // Stats & Analytics
    stats = {
      total: 0,
      pending: 0,
      approved: 0,
      rejected: 0,
      cancelled: 0,
      waitlist: 0,
      today: 0,
      thisWeek: 0,
      thisMonth: 0,
      religious: 0,
      political: 0,
      sporting: 0,
      equipment: 0,
      facilities: 0,
      memberships: 0,
      locationChanged: 0
    },
    
    analytics = {
      total: 0,
      byType: { facility: 0, equipment: 0, membership: 0 },
      byStatus: { pending: 0, approved: 0, rejected: 0, cancelled: 0, waitlist: 0, locationChanged: 0 },
      popularFacilities: [],
      communications: { sms: 0, whatsapp: 0, email: 0 },
      byDay: {}
    },
    
    showAnalytics = false,
    setShowAnalytics = () => {},
    analyticsPeriod = 'month',
    setAnalyticsPeriod = () => {},
    
    // Utilities
    bookingCounter = 1000,
    generateBookingId = () => `RA-${Date.now()}`,
    loadAllData = () => {}
  } = useBookingData(user) || {};

  // ==========================================================================
  // PUBLIC API DATA FETCHING FROM SQL (NO AUTH REQUIRED)
  // ==========================================================================
  
  // PUBLIC function to fetch equipment from SQL (NO AUTH)
  const fetchPublicEquipment = async () => {
    const API_BASE_URL = process.env.NODE_ENV === 'development' ? '/api' : 'https://nlngraclub.com/nlnapi';
    
    try {
      console.log('📡 Fetching equipment from SQL via PUBLIC endpoint...');
      
      // PUBLIC endpoint - NO Authorization header needed!
      const response = await fetch(`${API_BASE_URL}/equipment.php?action=list&_t=${Date.now()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
          // IMPORTANT: Do NOT include Authorization header here
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📦 Equipment API Response:', data);
      
      if (data.success && data.equipment) {
        const equipment = data.equipment;
        console.log(`✅ Loaded ${equipment.length} equipment items from SQL`);
        setEquipmentData(equipment);
        return equipment;
      } else {
        console.warn('⚠️ No equipment data received or API returned error:', data);
        setEquipmentData([]);
        return [];
      }
    } catch (error) {
      console.error('❌ Error fetching equipment:', error);
      setEquipmentData([]);
      return [];
    }
  };

  // Fetch facilities from SQL (already public via bookingService)
  const fetchFacilitiesFromSQL = async () => {
    try {
      console.log('📡 Fetching facilities from SQL...');
      const response = await bookingService.getFacilities();
      console.log('📡 Facilities response:', response);
      if (response.success) {
        setFacilitiesData(response.facilities || []);
        console.log('✅ Facilities loaded from SQL:', response.facilities?.length || 0);
        return response.facilities || [];
      } else {
        console.error('❌ Facilities failed:', response.message);
        setFacilitiesData([]);
        return [];
      }
    } catch (error) {
      console.error('❌ Facilities exception:', error);
      setFacilitiesData([]);
      return [];
    }
  };

  // Fetch sections from SQL using PUBLIC endpoint
  const fetchSectionsFromSQL = async () => {
    try {
      console.log('📡 Fetching sections from SQL...');
      const response = await sectionService.getAllSectionsPublic();
      console.log('📡 Sections response:', response);
      if (response.success && response.sections) {
        console.log('✅ Sections loaded from SQL:', response.sections.length);
        setSectionsData(response.sections || []);
        return response.sections || [];
      } else {
        console.warn('⚠️ No sections found in SQL');
        setSectionsData([]);
        return [];
      }
    } catch (error) {
      console.error('❌ Sections exception:', error);
      setSectionsData([]);
      return [];
    }
  };

  // Fetch lookup data from SQL
  const fetchLookupDataFromSQL = async () => {
    try {
      console.log('📡 Fetching lookup data from SQL...');
      const response = await bookingService.getLookupData();
      console.log('📡 Lookup response:', response);
      if (response.success) {
        setLookupData({
          jerseySizes: response.data?.jersey_sizes || [],
          timeSlots: response.data?.time_slots || [],
          eventTypes: response.data?.event_types || [],
          bookingStatuses: response.data?.booking_statuses || []
        });
        console.log('✅ Lookup data loaded from SQL');
        return response.data;
      } else {
        console.warn('⚠️ Lookup data failed:', response.message);
        return null;
      }
    } catch (error) {
      console.warn('⚠️ Lookup data exception:', error);
      return null;
    }
  };

  // Fetch all data from SQL database
  const fetchAllData = async () => {
    setLoading(true);
    
    console.log('🔍 Starting to fetch all data from SQL database...');
    
    try {
      // Fetch all data in parallel for better performance
      const [facilities, sections, equipment, lookup] = await Promise.all([
        fetchFacilitiesFromSQL(),
        fetchSectionsFromSQL(),
        fetchPublicEquipment(), // Using public endpoint now
        fetchLookupDataFromSQL()
      ]);
      
      const totalItems = (facilities?.length || 0) + (sections?.length || 0) + (equipment?.length || 0);
      console.log(`✅ Total items loaded from SQL: ${totalItems}`);
      
      if (totalItems === 0) {
        addNotification('No data found in database. Please contact administrator.', 'warning');
      } else {
        addNotification(`Loaded ${totalItems} items from database`, 'success');
      }
      
    } catch (error) {
      console.error('🔥 Fatal error in fetchAllData:', error);
      addNotification(`Failed to load data: ${error.message || 'Unknown error'}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Test API connectivity for debugging
  const testApiConnectivity = useCallback(async () => {
    console.log('🔍 Testing API connectivity...');
    
    const baseUrl = process.env.NODE_ENV === 'development' 
      ? '/api' 
      : 'https://nlngraclub.com/nlnapi';
    
    console.log('🔍 Testing with base URL:', baseUrl);
    
    try {
      const testUrl = `${baseUrl}/sections.php?action=public_list&_=${Date.now()}`;
      console.log('🔍 Test URL:', testUrl);
      
      const response = await fetch(testUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      console.log('🔍 Response status:', response.status);
      const text = await response.text();
      console.log('🔍 Response preview:', text.substring(0, 300));
      
      if (response.ok) {
        try {
          const json = JSON.parse(text);
          console.log('✅ API is reachable, sections found:', json.sections?.length || 0);
        } catch (e) {
          console.error('❌ Response is not valid JSON:', e);
        }
      } else {
        console.error('❌ API returned error status:', response.status);
      }
    } catch (error) {
      console.error('❌ Cannot reach API:', error);
    }
  }, []);

  // ==========================================================================
  // RAFFLE POPUP CHECK
  // ==========================================================================
  
  const checkRaffleEvents = useCallback(async () => {
    try {
      const response = await raffleService.getActiveEvents();
      if (response.success && response.events && response.events.length > 0) {
        // Find events with upcoming draws within 24 hours
        const upcomingEvents = response.events.filter(event => {
          if (!event.draw_date) return false;
          const drawDate = new Date(event.draw_date);
          const now = new Date();
          const hoursDiff = (drawDate - now) / (1000 * 60 * 60);
          return hoursDiff > 0 && hoursDiff <= 24;
        });
        
        if (upcomingEvents.length > 0 && !showRafflePopup && !localStorage.getItem('raffle_popup_dismissed_' + upcomingEvents[0]?.id)) {
          setActiveRaffleEvent(upcomingEvents[0]);
          setShowRafflePopup(true);
        }
      }
    } catch (error) {
      console.error('Check raffle events error:', error);
    }
  }, [showRafflePopup]);

  // ==========================================================================
  // EFFECTS
  // ==========================================================================
  
  // Effect 1: Load user from localStorage and fetch data from SQL
  useEffect(() => {
    const loadUserAndData = async () => {
      const userStr = localStorage.getItem('nlngra_user');
      if (userStr) {
        try {
          const userData = JSON.parse(userStr);
          setUser(userData);
          
          // Set default phone from user data
          if (userData.phone && !bookingData.phone) {
            setBookingData(prev => ({ ...prev, phone: userData.phone }));
          }
          
          // Fetch user role from API
          const roleResponse = await bookingService.getUserRole(userData.email);
          if (roleResponse.success && roleResponse.role) {
            setUserRoles({
              isAdmin: roleResponse.role.id === 'admin',
              isPresident: roleResponse.role.id === 'president',
              isVicePresident: roleResponse.role.id === 'vicePresident',
              isSecretary: roleResponse.role.id === 'secretary',
              isTreasurer: roleResponse.role.id === 'treasurer'
            });
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      }
      
      // Fetch all data from SQL
      await fetchAllData();
    };
    
    loadUserAndData();
    
    // Run connectivity test in production only
    if (process.env.NODE_ENV === 'production') {
      testApiConnectivity();
    }
  }, []);

  // Effect 2: Load data when user changes
  useEffect(() => {
    if (user?.id && loadAllData) {
      loadAllData();
    }
  }, [user?.id]);

  // Effect 3: Check for raffle events periodically
  useEffect(() => {
    if (user?.id) {
      checkRaffleEvents();
      const interval = setInterval(checkRaffleEvents, 300000); // Check every 5 minutes
      return () => clearInterval(interval);
    }
  }, [user?.id, checkRaffleEvents]);

  // Close menu when clicking outside on desktop
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (mobileMenuOpen && !e.target.closest('.booking-app__nav-slideout') && !e.target.closest('.booking-app__mobile-menu-btn-3d')) {
        setMobileMenuOpen(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [mobileMenuOpen]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // ==========================================================================
  // HELPER FUNCTIONS
  // ==========================================================================
  
  // Get user's first name safely
  const getUserFirstName = useCallback(() => {
    if (user?.first_name) return user.first_name;
    if (user?.name) return user.name.split(' ')[0];
    return 'User';
  }, [user]);

  // Get user's full name
  const getUserFullName = useCallback(() => {
    if (user?.first_name) {
      return user.last_name ? `${user.first_name} ${user.last_name}` : user.first_name;
    }
    if (user?.name) return user.name;
    return 'User';
  }, [user]);

  // Handle raffle popup close
  const handleRafflePopupClose = useCallback(() => {
    setShowRafflePopup(false);
    if (activeRaffleEvent?.id) {
      localStorage.setItem('raffle_popup_dismissed_' + activeRaffleEvent.id, 'true');
      // Clear after 24 hours
      setTimeout(() => {
        localStorage.removeItem('raffle_popup_dismissed_' + activeRaffleEvent.id);
      }, 24 * 60 * 60 * 1000);
    }
  }, [activeRaffleEvent]);

  // Handle raffle popup purchase
  const handleRafflePopupPurchase = useCallback((event) => {
    setActiveTab('raffle');
    setShowRafflePopup(false);
  }, []);

  // Handle Sign Out - Redirect to main website
  const handleSignOut = useCallback(() => {
    // Clear local storage
    localStorage.removeItem('nlngra_token');
    localStorage.removeItem('nlngra_user');
    localStorage.removeItem('nlngra_session');
    
    // Redirect to main website homepage
    window.location.href = 'https://nlngraclub.com/frontend/build/';
  }, []);

  // ==========================================================================
  // MEMOIZED VALUES
  // ==========================================================================
  
  const equipmentByCategory = useMemo(() => {
    return (equipmentData || []).reduce((acc, item) => {
      const category = item.category || 'Uncategorized';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(item);
      return acc;
    }, {});
  }, [equipmentData]);

  const isLeadership = useMemo(() => {
    const { isAdmin, isPresident, isVicePresident, isSecretary, isTreasurer } = userRoles;
    return isAdmin || isPresident || isVicePresident || isSecretary || isTreasurer;
  }, [userRoles]);

  const userRole = useMemo(() => {
    const { isPresident, isVicePresident, isSecretary, isTreasurer, isAdmin } = userRoles;
    
    if (isPresident) return { label: 'PRESIDENT', color: '#00843D' };
    if (isVicePresident) return { label: 'VICE PRESIDENT', color: '#00843D' };
    if (isSecretary) return { label: 'SECRETARY', color: '#00843D' };
    if (isTreasurer) return { label: 'TREASURER', color: '#00843D' };
    if (isAdmin) return { label: 'ADMIN', color: '#00843D' };
    return null;
  }, [userRoles]);

  // Nav items
  const navItems = useMemo(() => [
    { id: 'facilities', label: 'FACILITIES', icon: 'fa-regular fa-building', count: facilitiesData?.length },
    { id: 'sections', label: 'SECTIONS', icon: 'fa-regular fa-users', count: sectionsData?.length },
    { id: 'equipment', label: 'EQUIPMENT', icon: 'fa-solid fa-screwdriver-wrench' },
    { id: 'raffle', label: 'RAFFLE', icon: 'fa-solid fa-trophy', count: null },
    ...(user ? [{ id: 'raffle-tickets', label: 'BUY TICKETS', icon: 'fa-solid fa-ticket', count: null }] : []),
    ...(isLeadership ? [{ id: 'admin', label: 'ADMIN', icon: 'fa-solid fa-gear' }] : []),
    { id: 'my-bookings', label: 'MY BOOKINGS', icon: 'fa-regular fa-calendar-check', count: myBookings?.length }
  ], [facilitiesData?.length, sectionsData?.length, user, isLeadership, myBookings?.length]);

  // ==========================================================================
  // FILTER FUNCTIONS
  // ==========================================================================
  
  const filteredBookings = useCallback(() => {
    let filtered = [];
    
    if (filterStatus === 'all') {
      filtered = [
        ...(pendingBookings || []),
        ...(approvedBookings || []),
        ...(rejectedBookings || []),
        ...(cancelledBookings || []),
        ...(waitlistBookings || [])
      ];
    } else {
      const statusMap = {
        pending: pendingBookings,
        approved: approvedBookings,
        rejected: rejectedBookings,
        cancelled: cancelledBookings,
        waitlist: waitlistBookings
      };
      filtered = statusMap[filterStatus] || [];
    }

    if (filterType !== 'all' && filtered.length > 0) {
      filtered = filtered.filter(booking => booking?.type === filterType);
    }

    if (filterFacility !== 'all' && filtered.length > 0) {
      filtered = filtered.filter(booking => booking?.facilityId === parseInt(filterFacility));
    }

    if (searchTerm && filtered.length > 0) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(booking => 
        booking?.name?.toLowerCase().includes(term) ||
        booking?.requestId?.toLowerCase().includes(term) ||
        booking?.bookedBy?.toLowerCase().includes(term) ||
        booking?.requestorName?.toLowerCase().includes(term)
      );
    }

    if (dateRange?.start && filtered.length > 0) {
      filtered = filtered.filter(booking => 
        booking?.date && new Date(booking.date) >= new Date(dateRange.start)
      );
    }

    if (dateRange?.end && filtered.length > 0) {
      filtered = filtered.filter(booking => 
        booking?.date && new Date(booking.date) <= new Date(dateRange.end)
      );
    }

    if (filtered.length > 0) {
      filtered.sort((a, b) => {
        switch(sortBy) {
          case 'date_desc':
            return new Date(b?.date || 0) - new Date(a?.date || 0);
          case 'date_asc':
            return new Date(a?.date || 0) - new Date(b?.date || 0);
          case 'name_asc':
            return (a?.name || '').localeCompare(b?.name || '');
          case 'name_desc':
            return (b?.name || '').localeCompare(a?.name || '');
          default:
            return 0;
        }
      });
    }

    return filtered;
  }, [
    filterStatus, filterType, filterFacility, searchTerm, dateRange, sortBy,
    pendingBookings, approvedBookings, rejectedBookings, cancelledBookings, waitlistBookings
  ]);

  // ==========================================================================
  // UTILITY FUNCTIONS
  // ==========================================================================
  
  const getGreeting = useCallback(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  }, []);

  const togglePresidentDelegation = useCallback(() => {
    setPresidentDelegation(!presidentDelegation);
    if (addAuditLog) {
      addAuditLog('Delegation Toggled', { enabled: !presidentDelegation });
    }
  }, [presidentDelegation, setPresidentDelegation, addAuditLog]);

  // ==========================================================================
  // RENDER FUNCTIONS
  // ==========================================================================
  
  const renderContent = () => {
    const props = {
      user,
      setUser,
      addNotification,
      addAuditLog,
      generateBookingId,
      equipmentByCategory,
      sections: sectionsData,
      sectionMembers,
      equipmentCatalog: equipmentData,
      facilities: facilitiesData,
      pendingBookings,
      pendingMembers,
      waitlistBookings,
      filteredBookings: filteredBookings(),
      stats,
      analytics,
      showAnalytics,
      setShowAnalytics,
      analyticsPeriod,
      setAnalyticsPeriod,
      filterStatus,
      setFilterStatus,
      filterType,
      setFilterType,
      filterFacility,
      setFilterFacility,
      searchTerm,
      setSearchTerm,
      dateRange,
      setDateRange,
      sortBy,
      setSortBy,
      selectedBookings,
      setSelectedBookings,
      toggleSelectBooking,
      toggleSelectAll,
      addToWaitlist,
      removeFromWaitlist,
      promoteFromWaitlist,
      delegatedUsers,
      availableUsers: [],
      handleDelegate,
      auditLog,
      communicationHistory,
      sendSMS,
      sendWhatsApp,
      sendEmail,
      locationChangeHistory,
      handleLocationChange,
      conflicts,
      detectConflicts,
      resolveConflict,
      feedbacks,
      submitFeedback,
      respondToFeedback,
      maintenanceSchedule,
      scheduleMaintenance,
      cancelMaintenance,
      blackoutDates,
      addBlackoutDate,
      removeBlackoutDate,
      pricingRules,
      addPricingRule,
      updatePricingRule,
      deletePricingRule,
      permissions,
      updateRolePermissions,
      announcements,
      createAnnouncement,
      deleteAnnouncement,
      myBookings,
      jerseySizes: lookupData.jerseySizes,
      timeSlots: lookupData.timeSlots,
      eventTypes: lookupData.eventTypes,
      bookingStatuses: lookupData.bookingStatuses,
      ...userRoles,
      // Booking Modal Props - ADDED HERE
      bookingData,
      setBookingData,
      bookingStep,
      setBookingStep,
      selectedFacility,
      setSelectedFacility,
      showBookingModal,
      setShowBookingModal,
      requiresApproval,
      setRequiresApproval,
      approvalFile,
      setApprovalFile,
      approvalFileName,
      setApprovalFileName
    };

    const pages = {
      facilities: <FacilitiesPage {...props} />,
      sections: <SectionsPage {...props} />,
      equipment: <EquipmentPage {...props} />,
      raffle: <GeneralRafflePage user={user} addNotification={addNotification} />,
      'raffle-tickets': user ? <RafflePage user={user} addNotification={addNotification} /> : <div>Please login to buy tickets</div>,
      admin: isLeadership ? <AdminPage {...props} presidentDelegation={presidentDelegation} /> : null,
      'my-bookings': <MyBookingsPage {...props} />
    };

    return pages[activeTab] || pages.facilities;
  };

  if (loading) {
    return (
      <div className="booking-app-3d loading">
        <div className="loading-spinner"></div>
        <p>Loading resources from database...</p>
      </div>
    );
  }

  // ==========================================================================
  // MAIN RENDER
  // ==========================================================================
  
  return (
    <div className="booking-app-3d">
      {/* Premium Header */}
      <motion.header 
        className="booking-app__header-3d"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
      >
        <div className="booking-app__header-container-3d">
          <div className="booking-app__header-left-3d">
            <motion.div 
              className="booking-app__logo-container-3d"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
            >
              <div className="booking-app__logo-3d">
                <i className="fa-solid fa-bolt"></i>
              </div>
              <div className="booking-app__brand-text-3d">
                <h1>NLNGRA</h1>
                <span>Resource Management System</span>
              </div>
            </motion.div>
          </div>

          <div className="booking-app__header-right-3d">
            {/* Hamburger Menu Button - ALWAYS VISIBLE */}
            <motion.button 
              className="booking-app__mobile-menu-btn-3d"
              onClick={() => setMobileMenuOpen(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Open menu"
            >
              <i className="fa-solid fa-bars"></i>
            </motion.button>

            {/* Notifications */}
            <div className="booking-app__notifications-container-3d">
              <motion.button 
                className="booking-app__notifications-btn-3d"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowNotifications(!showNotifications)}
                aria-label="Notifications"
              >
                <i className="fa-regular fa-bell"></i>
                {notifications?.length > 0 && (
                  <motion.span 
                    className="booking-app__notif-badge-3d"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                  >
                    {notifications.length}
                  </motion.span>
                )}
              </motion.button>

              <AnimatePresence>
                {showNotifications && (
                  <>
                    <motion.div 
                      className="booking-app__overlay"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onClick={() => setShowNotifications(false)}
                    />
                    <motion.div 
                      className="booking-app__notifications-panel-3d"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ type: "spring", damping: 20 }}
                    >
                      <div className="booking-app__panel-header-3d">
                        <h3>Notifications</h3>
                        <button className="booking-app__btn-text-3d">Mark all read</button>
                      </div>
                      <div className="booking-app__panel-list-3d">
                        {notifications?.length > 0 ? (
                          notifications.map((notif, index) => (
                            <motion.div 
                              key={index} 
                              className="booking-app__notif-item-3d"
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.05 }}
                            >
                              <i className="fa-regular fa-bell"></i>
                              <div className="booking-app__notif-details-3d">
                                <p>{notif.message}</p>
                                <span className="booking-app__notif-time-3d">{notif.time}</span>
                              </div>
                            </motion.div>
                          ))
                        ) : (
                          <div className="booking-app__empty-state-3d">
                            <i className="fa-regular fa-bell-slash"></i>
                            <p>No notifications</p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>

            {/* User Menu - ONLY Sign Out (removed Profile, Settings, Analytics) */}
            <div className="booking-app__user-container-3d">
              <motion.div 
                className="booking-app__user-trigger-3d"
                whileHover={{ y: -2 }}
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="booking-app__avatar-3d">
                  {getUserFirstName().charAt(0) || 'U'}
                </div>
                <div className="booking-app__user-details-3d">
                  <span className="booking-app__greeting-text-3d">{getGreeting()}</span>
                  <span className="booking-app__user-fullname-3d">
                    {getUserFullName()}
                  </span>
                </div>
                <i className={`fa-solid fa-chevron-down booking-app__chevron-3d ${showUserMenu ? 'open' : ''}`}></i>
              </motion.div>

              <AnimatePresence>
                {showUserMenu && (
                  <>
                    <motion.div 
                      className="booking-app__overlay"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onClick={() => setShowUserMenu(false)}
                    />
                    <motion.div 
                      className="booking-app__user-menu-panel-3d"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ type: "spring", damping: 20 }}
                    >
                      <div className="booking-app__menu-header-3d">
                        <div className="booking-app__menu-user-3d">
                          <div className="booking-app__menu-avatar-3d">
                            {getUserFirstName().charAt(0) || 'U'}
                          </div>
                          <div className="booking-app__menu-user-info-3d">
                            <span className="booking-app__menu-user-name-3d">{getUserFullName()}</span>
                            <span className="booking-app__menu-user-email-3d">{user?.email || 'user@nlngra.com'}</span>
                          </div>
                        </div>
                        {userRole && (
                          <motion.span 
                            className="booking-app__role-tag-3d"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                          >
                            {userRole.label}
                          </motion.span>
                        )}
                      </div>
                      
                      <div className="booking-app__menu-items-3d">
                        {/* ONLY Sign Out - Removed Profile, Settings, Analytics */}
                        <div className="booking-app__menu-divider-3d"></div>
                        <motion.div 
                          className="booking-app__menu-item-3d booking-app__menu-item--logout-3d"
                          whileHover={{ x: 4 }}
                          onClick={handleSignOut}
                        >
                          <i className="fa-regular fa-arrow-right-from-bracket"></i>
                          <span>Sign Out</span>
                        </motion.div>
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Slide-out Navigation Menu - RIGHT TO LEFT */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div 
              className="booking-app__menu-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.nav 
              className="booking-app__nav-slideout"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
            >
              <div className="booking-app__nav-header">
                <div className="booking-app__nav-header-content">
                  <div className="booking-app__nav-logo">
                    <i className="fa-solid fa-bolt"></i>
                    <span>NLNGRA</span>
                  </div>
                  <motion.button 
                    className="booking-app__nav-close"
                    onClick={() => setMobileMenuOpen(false)}
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <i className="fa-solid fa-xmark"></i>
                  </motion.button>
                </div>
                <div className="booking-app__nav-user-info">
                  <div className="booking-app__nav-avatar">
                    {getUserFirstName().charAt(0) || 'U'}
                  </div>
                  <div className="booking-app__nav-user-text">
                    <span className="booking-app__nav-greeting">{getGreeting()}</span>
                    <span className="booking-app__nav-name">{getUserFullName()}</span>
                  </div>
                </div>
              </div>
              
              <div className="booking-app__nav-items">
                {navItems.map((item, index) => (
                  <motion.button
                    key={item.id}
                    className={`booking-app__nav-item-slideout ${activeTab === item.id ? 'active' : ''}`}
                    onClick={() => {
                      setActiveTab(item.id);
                      setMobileMenuOpen(false);
                    }}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <i className={item.icon}></i>
                    <span className="booking-app__nav-label">{item.label}</span>
                    {item.count > 0 && (
                      <span className="booking-app__nav-counter">{item.count}</span>
                    )}
                  </motion.button>
                ))}
              </div>
              
              <div className="booking-app__nav-footer">
                <div className="booking-app__nav-divider"></div>
                <motion.button 
                  className="booking-app__nav-logout"
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSignOut}
                >
                  <i className="fa-regular fa-arrow-right-from-bracket"></i>
                  <span>Sign Out</span>
                </motion.button>
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="booking-app__main-3d">
        {/* Welcome Section - STATS CARDS REMOVED */}
        <motion.div 
          className="booking-app__welcome-section-3d"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div 
            className="booking-app__welcome-message-3d"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <h2>
              {getGreeting()}, <span className="booking-app__accent-3d">
                {getUserFirstName()}
              </span>
            </h2>
            <p>Manage your resources efficiently with our premium booking system</p>
          </motion.div>
          
          {/* STATS GRID - COMPLETELY REMOVED */}
        </motion.div>

        {/* Dynamic Page Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.3 }}
            className="booking-app__content-area-3d"
          >
            {renderContent()}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Floating Action Button */}
      <motion.button 
        className="booking-app__fab-3d"
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", damping: 15, stiffness: 200, delay: 0.5 }}
        whileHover={{ y: -5, scale: 1.1, rotate: 90 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Quick actions"
      >
        <i className="fa-solid fa-bolt"></i>
      </motion.button>

      {/* Raffle Countdown Popup */}
      {showRafflePopup && activeRaffleEvent && (
        <RaffleCountdownPopup 
          event={activeRaffleEvent}
          onClose={handleRafflePopupClose}
          onPurchase={handleRafflePopupPurchase}
        />
      )}
    </div>
  );
};

export default BookingPage;